/**
 * Checks if a hostname is in the trusted embed domains list using suffix matching.
 * Trusted domains are allowed to use allow-same-origin in iframes.
 *
 * Uses suffix matching to prevent attacks like "evilgoogle.com" matching "google".
 *
 * @example
 * isTrustedEmbedDomain("calendar.google.com", ["google.com"]) // true
 * isTrustedEmbedDomain("google.com", ["google.com"]) // true
 * isTrustedEmbedDomain("evilgoogle.com", ["google.com"]) // false
 */
export function isTrustedEmbedDomain(
  hostname: string,
  trustedDomains: string[],
): boolean {
  if (!trustedDomains || trustedDomains.length === 0) {
    return false;
  }

  const lowerHostname = hostname.toLowerCase();

  return trustedDomains.some((domain) => {
    const lowerDomain = domain.toLowerCase();
    // Exact match or subdomain (ends with .domain)
    return (
      lowerHostname === lowerDomain || lowerHostname.endsWith(`.${lowerDomain}`)
    );
  });
}

/**
 * Generates sandbox attribute value based on domain trust.
 * Trusted domains get allow-same-origin, untrusted don't.
 *
 * Same-origin URLs are always trusted (e.g. zapier.com/shared/... embedded in
 * zapier.com/app/canvas/...) so they can access cookies and other same-origin
 * APIs needed for proper rendering.
 *
 * SECURITY: allow-same-origin + allow-scripts enables sandbox escape — the
 * embedded document can reach window.parent, modify the parent DOM, or remove
 * the sandbox from its own iframe. This is an accepted tradeoff for trusted
 * content (same-origin or trusted_embed_domains). Do not add new origins
 * without understanding this risk; an XSS in a trusted embed could affect
 * the parent Canvas page.
 *
 * @param url - The URL to check (extracts hostname/origin internally)
 * @param trustedDomains - List of trusted domain strings
 * @param currentOrigin - Optional. When provided, same-origin URLs are treated as trusted.
 */
export function getSandboxAttribute(
  url: string,
  trustedDomains: string[],
  currentOrigin?: string,
): string {
  let hostname = "";
  let embedOrigin = "";
  try {
    const parsed = new URL(url);
    hostname = parsed.hostname;
    embedOrigin = parsed.origin;
  } catch {
    // Invalid URL - treat as untrusted
  }

  const isSameOrigin =
    currentOrigin && embedOrigin && embedOrigin === currentOrigin;
  const isTrustedByConfig = isTrustedEmbedDomain(hostname, trustedDomains);
  const isTrusted = isSameOrigin || isTrustedByConfig;

  const baseSandbox = "allow-scripts allow-forms allow-popups";

  return isTrusted ? `${baseSandbox} allow-same-origin` : baseSandbox;
}
